import {
  CustomMappingType,
  type ICustomMapping,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { WithRef, multiMap } from '@principle-theorem/shared';
import { sortBy, uniqBy } from 'lodash';
import { scan } from 'rxjs/operators';
import { CustomMapping } from '../../../custom-mapping';
import {
  BaseReferralSourceMappingHandler,
  IBaseMigrationReferralSource,
} from '../../../mappings/base-referral-source';
import {
  ContactsSourceEntity,
  ExactContact,
  type IExactContact,
} from '../../source/entities/contacts';

export const CONTACT_SOURCE_CUSTOM_RESOURCE_TYPE = 'contacts';

export const CONTACT_SOURCES_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Contacts Mapping',
    description: `This allows us to map Exact's contacts to patient referral source`,
    type: CONTACT_SOURCE_CUSTOM_RESOURCE_TYPE,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Contact Name',
    destinationIdentifier: 'Referral Source',
  },
});

export class ExactContactMappingHandler extends BaseReferralSourceMappingHandler<
  ExactContact,
  ContactsSourceEntity
> {
  override customMapping = CONTACT_SOURCES_MAPPING;
  sourceEntity = new ContactsSourceEntity();

  translateFn = (record: IExactContact): IBaseMigrationReferralSource => {
    return { ...record, name: record.name ?? '' };
  };

  override async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .pipe(
        multiMap((record) => record.data),
        scan(
          (sources, newSources) => [...sources, ...newSources],
          [] as IExactContact[]
        )
      )
      .toPromise();

    const options = uniqBy(
      records.map((source) => ({
        label: this.sourceEntity.getSourceLabel(source),
        value: source.id,
      })),
      (option) => option.value
    );

    return sortBy(options, 'label');
  }
}
